'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import TeamMember from './TeamMember';
import MethodologyStep from './MethodologyStep';
import ValueCard from './ValueCard';
import MetricCard from './MetricCard';
import CertificationBadge from './CertificationBadge';
import TimelineEvent from './TimelineEvent';
import TeamMemberModal from './TeamMemberModal';

interface TeamMemberData {
  id: number;
  name: string;
  role: string;
  specialization: string;
  image: string;
  alt: string;
  certifications: string[];
  linkedin: string;
  email: string;
  bio: string;
  expertise: string[];
  achievements: string[];
  imagePosition?: string;
}

const AboutUsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMemberData | null>(null);
  const [activeTab, setActiveTab] = useState('team');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const teamMembers: TeamMemberData[] = [
  {
    id: 1,
    name: "Edwin R. Langeweg",
    role: "AI Strategie",
    specialization: "AI Strategie & Digitale Transformatie",
    image: "/assets/images/Edwin-1773245449322.png",
    alt: "Edwin R. Langeweg, AI Strategie specialist bij AIM Agency",
    imagePosition: "center top",
    certifications: ["AI Strategy Certified", "Digital Transformation Expert", "Business AI Advisor"],
    linkedin: "https://linkedin.com",
    email: "edwin@aimagency.nl",
    bio: "Edwin R. Langeweg is de AI-strateeg van AIM Agency met diepgaande expertise in het vertalen van AI-mogelijkheden naar concrete bedrijfsstrategieën. Hij begeleidt MKB-bedrijven in de Benelux bij het ontwikkelen en uitvoeren van toekomstbestendige AI-strategieën die direct aansluiten op bedrijfsdoelstellingen. Edwin's aanpak combineert strategisch inzicht met praktische implementatiekennis.",
    expertise: [
    "AI Strategie & Roadmapping",
    "Digitale Transformatie",
    "Business Case Development",
    "Change Management",
    "AI Governance & Ethics",
    "Organisatieontwikkeling"],

    achievements: [
    "Ontwikkelde AI-strategieën voor 150+ MKB-bedrijven in Benelux",
    "Leidde digitale transformatietrajecten met aantoonbare ROI-verbetering",
    "Spreker op AI & Business Strategy conferenties in Nederland en België",
    "Erkend AI-strateeg met focus op praktische bedrijfstoepassingen"]

  },
  {
    id: 2,
    name: "Mark Janssen",
    role: "Lead AI Engineer",
    specialization: "Computer Vision & Automation",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_124ddf771-1765167525499.png",
    alt: "Professional man with short dark hair in gray suit smiling in corporate setting",
    certifications: ["Microsoft AI Engineer", "PyTorch Certified", "Azure AI Expert"],
    linkedin: "https://linkedin.com",
    email: "mark@aimagency.nl",
    bio: "Mark Janssen is een expert in computer vision en automation met een achtergrond in industriële engineering. Hij heeft meer dan 10 jaar ervaring in het ontwikkelen van AI-oplossingen voor productie en logistiek. Mark's specialiteit ligt in het vertalen van complexe technische uitdagingen naar praktische, schaalbare AI-systemen die direct waarde toevoegen aan bedrijfsprocessen.",
    expertise: [
    "Computer Vision Systemen",
    "Process Automation",
    "Edge AI Deployment",
    "Real-time Object Detection",
    "Quality Control AI",
    "Robotics Integration"],

    achievements: [
    "Implementeerde AI-kwaliteitscontrole bij 50+ productiebedrijven",
    "Ontwikkelde real-time defect detection systeem met 99.2% nauwkeurigheid",
    "Patent pending voor innovative edge AI architecture",
    "Reduceerde productiefouten met gemiddeld 87% bij klanten"]

  },
  {
    id: 3,
    name: "Lisa de Vries",
    role: "AI Solutions Architect",
    specialization: "Enterprise AI & Integration",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3a1efe8-1772875264718.png",
    alt: "Professional woman with brown hair in white blouse smiling warmly in bright office",
    certifications: ["Salesforce AI Specialist", "IBM Watson Certified", "SAP AI Expert"],
    linkedin: "https://linkedin.com",
    email: "lisa@aimagency.nl",
    bio: "Lisa de Vries combineert technische expertise met diepgaande kennis van bedrijfsprocessen. Met een achtergrond in enterprise software en 8 jaar ervaring in AI-integratie, zorgt Lisa ervoor dat AI-oplossingen naadloos aansluiten op bestaande systemen. Ze is gespecialiseerd in het ontwerpen van schaalbare AI-architecturen die groeien met het bedrijf.",
    expertise: [
    "Enterprise AI Architecture",
    "System Integration",
    "CRM AI Enhancement",
    "Data Pipeline Design",
    "Cloud AI Solutions",
    "Legacy System Modernization"],

    achievements: [
    "Integreerde AI in 100+ bestaande enterprise systemen",
    "Ontwikkelde modulaire AI-framework voor snelle deployment",
    "Reduceerde implementatietijd met gemiddeld 60%",
    "Certified in alle major cloud platforms (AWS, Azure, GCP)"]

  },
  {
    id: 4,
    name: "Thomas Bakker",
    role: "Data Science Lead",
    specialization: "Predictive Analytics & BI",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f3374efb-1763296851960.png",
    alt: "Professional man with glasses and beard in blue shirt smiling in modern workspace",
    certifications: ["Data Science Professional", "Tableau Expert", "Power BI Specialist"],
    linkedin: "https://linkedin.com",
    email: "thomas@aimagency.nl",
    bio: "Thomas Bakker is een data science expert met een passie voor het ontdekken van waardevolle inzichten in complexe datasets. Met een PhD in statistiek en 12 jaar ervaring in business intelligence, helpt Thomas bedrijven om data-gedreven beslissingen te nemen. Zijn predictive models hebben klanten geholpen om omzet te verhogen en kosten te verlagen met meetbare resultaten.",
    expertise: [
    "Predictive Modeling",
    "Business Intelligence",
    "Statistical Analysis",
    "Data Visualization",
    "Forecasting Algorithms",
    "Customer Analytics"],

    achievements: [
    "Ontwikkelde voorspellingsmodellen met 94% nauwkeurigheid",
    "Hielp klanten €15M+ besparen door predictive maintenance",
    "Creëerde 200+ custom dashboards voor real-time insights",
    "Publiceerde 15+ papers over praktische AI-toepassingen"]

  },
  {
    id: 5,
    name: "Xamaica van der Kleij",
    role: "AI Marketing & Social Media Specialist",
    specialization: "AI Marketing & AI Social Media Automation",
    image: "/assets/images/Xamaica-1773245819300.jpeg",
    alt: "Xamaica van der Kleij, AI Marketing & Social Media Specialist",
    certifications: ["AI Marketing Certified", "Social Media Automation Expert", "Digital Marketing AI"],
    linkedin: "https://linkedin.com",
    email: "xamaica@aimagency.nl",
    bio: "Xamaica van der Kleij is gespecialiseerd in AI-gedreven marketing en social media automatisering. Met diepgaande kennis van AI-marketingstrategieën en geautomatiseerde social media workflows helpt ze bedrijven hun online aanwezigheid te versterken en marketingprocessen te optimaliseren. Xamaica combineert creativiteit met data-gedreven AI-inzichten voor maximale impact.",
    expertise: [
    "AI Marketing Strategy",
    "Social Media Automation",
    "AI Content Generation",
    "Marketing Analytics",
    "Campaign Automation",
    "Brand Growth via AI"],

    achievements: [
    "Verhoogde social media engagement met gemiddeld 70% via AI-automatisering",
    "Ontwikkelde AI-marketingcampagnes die ROI met 3x verbeterden",
    "Automatiseerde social media workflows voor 50+ bedrijven",
    "Implementeerde AI-contentstrategieën die bereik verdubbelden"]

  },
  {
    id: 6,
    name: "David Smit",
    role: "AI Security Specialist",
    specialization: "AI Ethics & Compliance",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14a5ca983-1763300171126.png",
    alt: "Professional man with short hair in dark suit looking serious in office setting",
    certifications: ["CISSP Certified", "GDPR Expert", "AI Ethics Certified"],
    linkedin: "https://linkedin.com",
    email: "david@aimagency.nl",
    bio: "David Smit zorgt ervoor dat alle AI-implementaties voldoen aan de hoogste veiligheids- en privacy-standaarden. Met een achtergrond in cybersecurity en 10 jaar ervaring in compliance, begeleidt David bedrijven door het complexe landschap van AI-regelgeving. Hij is een erkend expert in GDPR-compliance voor AI-systemen en ethische AI-ontwikkeling.",
    expertise: [
    "AI Security Architecture",
    "GDPR Compliance",
    "Ethical AI Development",
    "Data Privacy Protection",
    "Risk Assessment",
    "Regulatory Compliance"],

    achievements: [
    "Zorgde voor 100% GDPR-compliance bij alle AI-implementaties",
    "Ontwikkelde security framework voor AI-systemen",
    "Adviseur voor Nederlandse AI-regelgeving commissie",
    "Zero security breaches in 5+ jaar bij alle klantprojecten"]

  }];


  const methodologySteps = [
  {
    id: 1,
    number: "01",
    title: "AI Readiness Scan",
    description: "Grondige analyse van uw huidige processen, data-infrastructuur en organisatiecultuur om AI-mogelijkheden te identificeren.",
    icon: "MagnifyingGlassIcon",
    duration: "1-2 weken"
  },
  {
    id: 2,
    number: "02",
    title: "Strategische Planning",
    description: "Ontwikkeling van een op maat gemaakte AI-roadmap met duidelijke doelstellingen, KPI's en ROI-projecties.",
    icon: "MapIcon",
    duration: "2-3 weken"
  },
  {
    id: 3,
    number: "03",
    title: "Proof of Concept",
    description: "Snelle ontwikkeling van een werkend prototype om waarde te demonstreren en buy-in te creëren binnen uw organisatie.",
    icon: "BeakerIcon",
    duration: "3-4 weken"
  },
  {
    id: 4,
    number: "04",
    title: "Implementatie",
    description: "Volledige uitrol van de AI-oplossing met naadloze integratie in uw bestaande systemen en workflows.",
    icon: "CogIcon",
    duration: "6-12 weken"
  },
  {
    id: 5,
    number: "05",
    title: "Training & Adoptie",
    description: "Uitgebreide training van uw team en change management om maximale adoptie en effectiviteit te garanderen.",
    icon: "AcademicCapIcon",
    duration: "2-4 weken"
  },
  {
    id: 6,
    number: "06",
    title: "Optimalisatie & Support",
    description: "Continue monitoring, optimalisatie en support om ervoor te zorgen dat uw AI-oplossing blijft presteren en meegroeien met uw bedrijf.",
    icon: "ArrowTrendingUpIcon",
    duration: "Doorlopend"
  }];


  const companyValues = [
  {
    id: 1,
    icon: "LightBulbIcon",
    title: "Innovatie met Impact",
    description: "We geloven in AI-oplossingen die niet alleen technisch geavanceerd zijn, maar ook directe, meetbare waarde toevoegen aan uw bedrijf.",
    color: "primary"
  },
  {
    id: 2,
    icon: "UserGroupIcon",
    title: "Mensgerichte Technologie",
    description: "AI moet mensen versterken, niet vervangen. We ontwerpen oplossingen die uw team empoweren en klanten beter bedienen.",
    color: "secondary"
  },
  {
    id: 3,
    icon: "ShieldCheckIcon",
    title: "Transparantie & Vertrouwen",
    description: "We zijn open over wat AI wel en niet kan, zorgen voor GDPR-compliance en bouwen systemen die u kunt vertrouwen.",
    color: "accent"
  },
  {
    id: 4,
    icon: "RocketLaunchIcon",
    title: "Pragmatische Aanpak",
    description: "Geen complexe jargon of onnodige features. We focussen op wat werkt en leveren resultaten die u kunt meten.",
    color: "success"
  }];


  const metrics = [
  {
    id: 1,
    icon: "BuildingOfficeIcon",
    value: "200",
    label: "Tevreden Klanten",
    suffix: "+"
  },
  {
    id: 2,
    icon: "ClockIcon",
    value: "50K",
    label: "Uren Bespaard",
    suffix: "+"
  },
  {
    id: 3,
    icon: "ChartBarIcon",
    value: "87",
    label: "Gemiddelde ROI",
    suffix: "%"
  },
  {
    id: 4,
    icon: "StarIcon",
    value: "4.9",
    label: "Klantbeoordeling",
    suffix: "/5"
  }];


  const certifications = [
  {
    id: 1,
    name: "Google Cloud Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_167ac8805-1773246401194.png",
    alt: "Google Cloud Partner certification badge with blue and white colors",
    category: "Cloud AI"
  },
  {
    id: 2,
    name: "AWS Advanced Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1efd3e616-1773246400259.png",
    alt: "AWS Advanced Partner certification badge with orange and black colors",
    category: "Machine Learning"
  },
  {
    id: 3,
    name: "Microsoft AI Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c8277775-1773246399870.png",
    alt: "Microsoft AI Partner certification badge with blue gradient",
    category: "Azure AI"
  },
  {
    id: 4,
    name: "ISO 27001 Certified",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_144a19382-1773246403032.png",
    alt: "ISO 27001 information security certification badge",
    category: "Security"
  },
  {
    id: 5,
    name: "GDPR Compliant",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_100029a2d-1767469405066.png",
    alt: "GDPR compliance certification badge with EU stars",
    category: "Privacy"
  },
  {
    id: 6,
    name: "TensorFlow Certified",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_142a33ee4-1765007859464.png",
    alt: "TensorFlow developer certification badge with orange logo",
    category: "Deep Learning"
  }];


  const timeline = [
  {
    id: 1,
    year: "2019",
    title: "Oprichting AIM Agency",
    description: "Dr. Sophie van der Berg richt AIM Agency op met de missie om AI toegankelijk te maken voor MKB-bedrijven in de Benelux.",
    icon: "RocketLaunchIcon"
  },
  {
    id: 2,
    year: "2020",
    title: "Eerste 50 Klanten",
    description: "Succesvolle implementatie van AI-oplossingen bij 50 MKB-bedrijven, met gemiddelde ROI van 65% in het eerste jaar.",
    icon: "UserGroupIcon"
  },
  {
    id: 3,
    year: "2021",
    title: "Uitbreiding Team",
    description: "Groei naar 15 AI-specialisten en opening van tweede kantoor in Brussel om Belgische markt beter te bedienen.",
    icon: "BuildingOfficeIcon"
  },
  {
    id: 4,
    year: "2022",
    title: "Proprietary AI Framework",
    description: "Lancering van eigen AI-framework specifiek ontworpen voor Nederlandse taal en Benelux bedrijfscultuur.",
    icon: "CpuChipIcon"
  },
  {
    id: 5,
    year: "2023",
    title: "100+ Succesvolle Projecten",
    description: "Mijlpaal van 100 succesvolle AI-implementaties met totale kostenbesparing van €25M+ voor klanten.",
    icon: "TrophyIcon"
  },
  {
    id: 6,
    year: "2024",
    title: "AI Innovator Award",
    description: "Winnaar van \'AI Innovator of the Year\' Benelux en erkenning als leading AI consultancy voor MKB.",
    icon: "StarIcon"
  },
  {
    id: 7,
    year: "2025",
    title: "200+ Tevreden Klanten",
    description: "Bereiken van 200 klanten milestone en uitbreiding naar 30+ AI-specialisten met focus op nieuwe AI-technologieën.",
    icon: "SparklesIcon"
  }];


  const handleViewDetails = (member: TeamMemberData) => {
    if (!isHydrated) return;
    setSelectedMember(member);
  };

  const handleCloseModal = () => {
    if (!isHydrated) return;
    setSelectedMember(null);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background">
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
        <div className="absolute top-20 right-20 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-semibold mb-6">
              <Icon name="SparklesIcon" size={16} />
              <span>Over AIM Agency</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-foreground mb-6">
              De AI-brug voor <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Benelux MKB</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We maken geavanceerde AI-technologie toegankelijk en direct waardevol voor traditionele bedrijven. Geen complexiteit, alleen resultaten die u kunt meten.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {metrics.map((metric) =>
            <MetricCard key={metric.id} metric={metric} />
            )}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              Onze <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Kernwaarden</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              De principes die ons werk en onze relaties met klanten vormgeven
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {companyValues.map((value) =>
            <ValueCard key={value.id} value={value} />
            )}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-gradient-to-b from-transparent to-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              Onze <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Methodologie</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Een bewezen aanpak die AI-transformatie eenvoudig en effectief maakt
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {methodologySteps.map((step, index) =>
            <MethodologyStep key={step.id} step={step} index={index} />
            )}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              Ons <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Expert Team</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Ontmoet de AI-specialisten die uw transformatie begeleiden
            </p>

            <div className="flex items-center justify-center gap-4 mb-12">
              <button
                onClick={() => setActiveTab('team')}
                className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeTab === 'team' ? 'bg-primary text-primary-foreground shadow-premium' : 'bg-muted text-muted-foreground hover:bg-primary/20 hover:text-primary'}`
                }>

                Team Members
              </button>
              <button
                onClick={() => setActiveTab('timeline')}
                className={`px-6 py-3 rounded-lg font-semibold text-sm transition-all duration-300 ${
                activeTab === 'timeline' ? 'bg-primary text-primary-foreground shadow-premium' : 'bg-muted text-muted-foreground hover:bg-primary/20 hover:text-primary'}`
                }>

                Ons Verhaal
              </button>
            </div>
          </div>

          {activeTab === 'team' &&
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {teamMembers.map((member) =>
            <TeamMember
              key={member.id}
              member={member}
              onViewDetails={handleViewDetails} />

            )}
            </div>
          }

          {activeTab === 'timeline' &&
          <div className="max-w-4xl mx-auto">
              {timeline.map((event, index) =>
            <TimelineEvent
              key={event.id}
              event={event}
              index={index}
              total={timeline.length} />

            )}
            </div>
          }
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-gradient-to-b from-muted/20 to-transparent">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6">
              Certificeringen & <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary">Partnerships</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Erkend door toonaangevende technologiepartners en compliance-organisaties
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
            {certifications.map((cert) =>
            <CertificationBadge key={cert.id} certification={cert} />
            )}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto glassmorphic rounded-2xl p-8 md:p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-6">
              Klaar om uw AI-transformatie te starten?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Ontdek hoe AIM Agency uw bedrijf kan helpen met praktische AI-oplossingen die direct waarde toevoegen.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="/contact"
                className="px-8 py-4 rounded-lg font-semibold text-lg bg-cta text-cta-foreground hover:bg-cta/90 transition-all duration-300 hover:shadow-elevated hover:-translate-y-1 flex items-center gap-2">

                <span>Gratis AI-scan aanvragen</span>
                <Icon name="ArrowRightIcon" size={20} />
              </Link>
              <Link
                href="/case-studies-hub"
                className="px-8 py-4 rounded-lg font-semibold text-lg border border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:shadow-premium">

                Bekijk case studies
              </Link>
            </div>
          </div>
        </div>
      </section>

      {selectedMember &&
      <TeamMemberModal member={selectedMember} onClose={handleCloseModal} />
      }
    </div>);

};

export default AboutUsInteractive;